<template>
  <div class="sl-root">
    <div class="sl-card">

      <div class="sl-brand">
        <div class="sl-brand-mark">T</div>
        <div>
          <div class="sl-brand-name">Tyng &amp; Co</div>
          <div class="sl-brand-sub">Staff Portal</div>
        </div>
      </div>

      <h2 class="sl-title">Welcome back</h2>
      <p class="sl-hint">Sign in with your staff credentials</p>

      <div class="sl-field">
        <label class="sl-label">Username</label>
        <div class="sl-input-wrap">
          <fa-icon :icon="['fas', 'user']" class="sl-input-icon" />
          <input
            v-model="username"
            type="text"
            class="sl-input"
            placeholder="Enter username"
            autocomplete="username"
            @keyup.enter="doLogin"
          />
        </div>
      </div>

      <div class="sl-field">
        <label class="sl-label">Password</label>
        <div class="sl-input-wrap">
          <fa-icon :icon="['fas', 'lock']" class="sl-input-icon" />
          <input
            v-model="password"
            :type="showPw ? 'text' : 'password'"
            class="sl-input sl-input--pw"
            placeholder="Enter password"
            autocomplete="current-password"
            @keyup.enter="doLogin"
          />
          <button class="sl-eye-btn" type="button" @click="showPw = !showPw">
            <fa-icon :icon="['fas', showPw ? 'eye' : 'eye-slash']" />
          </button>
        </div>
      </div>

      <div v-if="error" class="sl-error">
        <fa-icon :icon="['fas', 'exclamation-circle']" />
        <span>{{ error }}</span>
      </div>

      <button
        class="sl-login-btn"
        :disabled="!username.trim() || !password.trim() || loading"
        @click="doLogin"
      >
        <fa-icon v-if="loading" :icon="['fas', 'spinner']" spin />
        {{ loading ? 'Signing in...' : 'Sign In' }}
      </button>

      <div class="sl-footer">
        <router-link
          :to="{ name: 'Admin-Login', params: { lang: $route.params.lang } }"
          class="sl-footer-link"
        >
          Admin / Management Login
        </router-link>
      </div>

    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/services/api'
import { encryptPassword } from '@/services/crypto'

export default {
  name: 'staff-login',
  data () {
    return {
      username: '',
      password: '',
      showPw: false,
      loading: false,
      error: ''
    }
  },
  mounted () {
    if (localStorage.getItem('staffToken')) {
      this.$router.replace({ name: 'Staff-Panel', params: { lang: this.$route.params.lang } })
    }
  },
  watch: {
    username () { if (this.error) this.error = '' },
    password () { if (this.error) this.error = '' }
  },
  methods: {
    async doLogin () {
      if (!this.username.trim() || !this.password.trim() || this.loading) return
      this.loading = true
      this.error = ''
      try {
        const encryptedPassword = await encryptPassword(this.password)
        const data = await apiFetch('/api/admin/login.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: this.username, encrypted_password: encryptedPassword })
        })
        if (data.success) {
          if (data.role !== 'staff') {
            this.error = 'This portal is for staff accounts only. Please use the Admin Login.'
            return
          }
          localStorage.setItem('staffToken', data.token)
          localStorage.setItem('staffRole', data.role)
          if (data.refresh_token) localStorage.setItem('staffRefreshToken', data.refresh_token)
          this.$router.push({ name: 'Staff-Panel', params: { lang: this.$route.params.lang } })
        } else {
          this.error = data.message || 'Invalid credentials'
        }
      } catch (err) {
        this.error = err?.message || 'Login failed. Please try again.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.sl-root {
  min-height: 100vh;
  background: #f0f4fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Inter', sans-serif;
}

.sl-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, .10);
  padding: 40px 36px;
  width: 100%;
  max-width: 400px;
}

.sl-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.sl-brand-mark {
  width: 40px;
  height: 40px;
  background: #1a3c6e;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
}

.sl-brand-name {
  font-weight: 700;
  font-size: 16px;
  color: #1e293b;
  line-height: 1.2;
}

.sl-brand-sub {
  font-size: 12px;
  color: #64748b;
}

.sl-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}

.sl-hint {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 28px;
}

.sl-field {
  margin-bottom: 16px;
}

.sl-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: .05em;
  margin-bottom: 6px;
}

.sl-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.sl-input-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}

.sl-input {
  width: 100%;
  padding: 11px 14px 11px 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: border-color .15s, background .15s;
  font-family: inherit;
  box-sizing: border-box;
}

.sl-input--pw { padding-right: 40px; }

.sl-input:focus {
  border-color: #1a3c6e;
  background: #fff;
}

.sl-eye-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  font-size: 13px;
  line-height: 1;
}

.sl-eye-btn:hover { color: #1a3c6e; }

.sl-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.sl-login-btn {
  width: 100%;
  padding: 12px;
  background: #1a3c6e;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  transition: background .15s;
  font-family: inherit;
}

.sl-login-btn:hover:not(:disabled) { background: #15305a; }

.sl-login-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sl-footer {
  text-align: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.sl-footer-link {
  font-size: 13px;
  color: #64748b;
  text-decoration: none;
}

.sl-footer-link:hover { color: #1a3c6e; }

@media (max-width: 480px) {
  .sl-card { padding: 32px 24px; }
}
</style>
